import { SegmentationMapping } from '@/types/components/audiences/segmentation-mapping'

interface FilterGroupLabelSource {
  /** The group's id — the key under which its alias is stored in customLabels. */
  groupId: string
  /** Member (source) columns the group alias should ride on. */
  memberColumns: Array<string>
}

/**
 * Apply edited filter labels onto segmentation mappings before persisting.
 *
 * Single-column aliases (keyed by `sourceColumn`) overwrite `displayLabel`.
 * Group aliases (keyed by the group's id, not a real column) are stamped as
 * `groupDisplayLabel` on every member column's mapping — mirroring the create-time
 * overlay so the study-creation filter modal can read the alias back. Group-id
 * keys never create synthetic mappings.
 */
export function applyFilterCustomLabels(
  mappings: Array<SegmentationMapping>,
  customLabels: Record<string, string>,
  groups: Array<FilterGroupLabelSource> = [],
): Array<SegmentationMapping> {
  const mappingBySource = new Map(
    mappings.map((mapping) => [mapping.sourceColumn, mapping]),
  )

  // Resolve group aliases (keyed by group id) into per-member-column overrides.
  // `touchedGroupIds` tracks only groups whose id appears in customLabels — we
  // must NOT clear a group alias just because another group was edited.
  const groupLabelBySource = new Map<string, string>()
  const groupMemberColumns = new Set<string>()
  const groupMemberToGroupId = new Map<string, string>()
  const groupIds = new Set<string>()
  const touchedGroupIds = new Set<string>()
  for (const group of groups) {
    groupIds.add(group.groupId)
    if (Object.prototype.hasOwnProperty.call(customLabels, group.groupId)) {
      touchedGroupIds.add(group.groupId)
    }
    const label = customLabels[group.groupId]?.trim()
    for (const column of group.memberColumns) {
      groupMemberColumns.add(column)
      groupMemberToGroupId.set(column, group.groupId)
      if (label) {
        groupLabelBySource.set(column, label)
      }
    }
  }

  const updatedMappings = mappings.map((mapping) => {
    const groupLabel = groupLabelBySource.get(mapping.sourceColumn)
    let withGroup = mapping
    if (groupLabel) {
      withGroup = { ...mapping, groupDisplayLabel: groupLabel }
    } else if (
      groupMemberColumns.has(mapping.sourceColumn) &&
      mapping.groupDisplayLabel &&
      touchedGroupIds.has(groupMemberToGroupId.get(mapping.sourceColumn) ?? '')
    ) {
      // The user explicitly cleared this group's alias — drop the stale label.
      const { groupDisplayLabel: _cleared, ...rest } = mapping
      withGroup = rest as SegmentationMapping
    }
    const newLabel = customLabels[withGroup.sourceColumn]
    if (newLabel) {
      return { ...withGroup, displayLabel: newLabel }
    }
    return withGroup
  })

  // Single-column aliases for columns not yet present get a real mapping.
  // Group-id keys are skipped — they only ever annotate existing member columns.
  for (const [sourceColumn, newLabel] of Object.entries(customLabels)) {
    if (groupIds.has(sourceColumn) || mappingBySource.has(sourceColumn)) {
      continue
    }
    updatedMappings.push({
      sourceColumn,
      canonicalId: null,
      displayLabel: newLabel,
      mappingSource: 'manual',
    } as SegmentationMapping)
  }

  return updatedMappings
}
